import { useCallback, useEffect, useState } from "react";
import { fetchEntitlements } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * Client-side entitlements — mirrors server-side gate in payments.py.
 * Falls back to a safe locked state until fetched.
 */
export function useEntitlements() {
  const { user } = useAuth();
  const [entitlements, setEntitlements] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user) {
      setEntitlements(null);
      setLoading(false);
      return null;
    }
    try {
      const data = await fetchEntitlements();
      setEntitlements(data);
      return data;
    } catch (e) {
      console.error(e);
      setEntitlements(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const canAccess = useCallback(
    (sectionId, promptIndex) => {
      if (!entitlements) return false;
      if (entitlements.subscription_active) return true;
      if ((entitlements.unlocked_chapters || []).includes(sectionId)) return true;
      if (
        sectionId === entitlements.free_chapter_id &&
        promptIndex < entitlements.free_prompt_count
      ) {
        return true;
      }
      return false;
    },
    [entitlements]
  );

  const chapterUnlocked = useCallback(
    (sectionId) => {
      if (!entitlements) return false;
      if (entitlements.subscription_active) return true;
      return (entitlements.unlocked_chapters || []).includes(sectionId);
    },
    [entitlements]
  );

  return { entitlements, loading, refresh, canAccess, chapterUnlocked };
}
